import React, { forwardRef, useImperativeHandle } from 'react';
import { UseFormProps } from 'react-hook-form';
import { SpinLoading } from '../Loading/SpinLoading';
import { DirtyFormContainer } from './DirtyFormContainer';
import { DisabledActionWrapper } from './DisabledActionWrapper';
import {
    DataChangedFunction,
    IDynamicFormGroup,
    IDynamicFormRef,
    IDynamicFormRow,
} from './DynamicForm.types';
import { useDynamicForm } from './hooks/useDynamicForm';

export interface IDynamicFormProps {
    /**Function to handle the submitting of data, returns back the forms data object */
    onHandleSubmit: (data: Record<string, any>) => void;
    /**call api function for the times that there is item id but the form data is undefined, its usefull for opening form using urls*/
    callApi?: any;
    /**call api loading*/
    isLoading?: any;
    /**form data id*/
    itemId?: string;
    /**buttons*/
    formFooter: React.ReactNode;
    /**Form rows */
    formRows?: IDynamicFormRow[];
    /** Form Groups */
    formGroups?: IDynamicFormGroup[];
    /**The form's pre-populated data */
    formData?: Record<string, any>;
    /**Form key name, keep these unique across forms */
    formKeyName: string;
    /**Some extend options for the use form validation */
    useFormOptions?: UseFormProps;
    disabledFields?: Record<string, Record<string, string[]>>;
    subscriptionKey?: string;
    disableAll?: boolean;
    disableAllInteraction?: Function;
    /**Function that returns back a boolean if the data has been changed */
    hasDataChanged?: DataChangedFunction;
    /**form container styles */
    containerClassNames?: string;
    /**form container styles */
    resetFormData?: boolean;
}
/**
 * Dynamic forms is a handy tool to help generate forms from a form schema
 *
 * @param props
 */

export const DynamicForm = forwardRef<IDynamicFormRef, IDynamicFormProps>(
    (props, ref) => {
        const {
            watch,
            setValue,
            getValues,
            reset,
            trigger,
            onSubmit,
            handleSubmit,
            control,
            createForm,
        } = useDynamicForm(props);

        useImperativeHandle(
            ref,
            () => ({
                watch,
                setValue,
                getValues,
                reset,
                trigger,
            }),
            [watch, setValue, getValues, reset, trigger]
        );

        return (
            <form
                key={props.formKeyName + '_mainform'}
                onSubmit={handleSubmit(onSubmit)}
                className="relative"
            >
                {props.isLoading && <SpinLoading />}
                <DisabledActionWrapper
                    disabled={props.disableAll}
                    onDisabledClick={props.disableAllInteraction}
                >
                    <div className={props.containerClassNames}>
                        {createForm()}
                    </div>
                    {props.formFooter}
                    {props.hasDataChanged && (
                        <DirtyFormContainer
                            control={control}
                            formData={props.formData}
                            hasDataChanged={props.hasDataChanged}
                        />
                    )}
                </DisabledActionWrapper>
            </form>
        );
    }
);

DynamicForm.displayName = 'DynamicForm';
